import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  AudiencePreviewResult,
  Campaign,
  CampaignDetailResponse,
  CampaignListParams,
  CampaignRecipientsParams,
  CampaignRecipientsResponse,
  CampaignsListResponse,
  Segment,
  SegmentOptions,
  SegmentMembersResult,
  SegmentsListResponse,
} from '@/client/types/campaigns';

const CAMPAIGNS = '/api/admin/v2/campaigns';
const SEGMENTS = '/api/admin/v2/segments';

export const lifecycleKeys = {
  all: ['campaign-lifecycle-v2'] as const,
  campaigns: () => [...lifecycleKeys.all, 'campaigns'] as const,
  campaignList: (p: object) => [...lifecycleKeys.campaigns(), 'list', p] as const,
  campaignDetail: (cid: string) => [...lifecycleKeys.campaigns(), 'detail', cid] as const,
  campaignRecipients: (cid: string, p: object) =>
    [...lifecycleKeys.campaigns(), 'recipients', cid, p] as const,
  campaignAudience: (cid: string) => [...lifecycleKeys.campaigns(), 'audience', cid] as const,
  segments: () => [...lifecycleKeys.all, 'segments'] as const,
  segmentList: () => [...lifecycleKeys.segments(), 'list'] as const,
  segmentDetail: (sid: string) => [...lifecycleKeys.segments(), 'detail', sid] as const,
};

const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

const unwrap = async <T>(promise: Promise<APIResponse<T>>): Promise<T> => {
  const res = await promise;
  return res.data as T;
};

const asCampaign = (raw: unknown): Campaign => {
  if (raw && typeof raw === 'object' && 'campaign' in raw) {
    const wrapped = raw as { campaign?: Campaign };
    if (wrapped.campaign) return wrapped.campaign;
  }
  return raw as Campaign;
};

const asSegment = (raw: unknown): Segment => {
  if (raw && typeof raw === 'object' && 'segment' in raw) {
    const wrapped = raw as { segment?: Segment };
    if (wrapped.segment) return wrapped.segment;
  }
  return raw as Segment;
};

// ─── Campaigns ────────────────────────────────────────────────────────────────

export const useCampaignListQuery = (params: CampaignListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<CampaignsListResponse>({
    queryKey: lifecycleKeys.campaignList(params),
    queryFn: withErrorHandling(async () => {
      return unwrap(
        request<APIResponse<CampaignsListResponse>>(
          `${CAMPAIGNS}${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch campaigns'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const rows = query.state.data?.campaigns ?? [];
      const live = rows.some((c) => c.status === 'running' || c.status === 'scheduled');
      return live ? 8_000 : false;
    },
  });
};

export const useCampaignDetailQuery = (cid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<Campaign>({
    queryKey: lifecycleKeys.campaignDetail(cid ?? ''),
    queryFn: withErrorHandling(async () => {
      const raw = await unwrap(
        request<APIResponse<CampaignDetailResponse | Campaign>>(`${CAMPAIGNS}/${cid}`),
      );
      return asCampaign(raw);
    }, 'Failed to fetch campaign'),
    enabled: enabled && !!cid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' || status === 'scheduled' ? 5_000 : false;
    },
  });
};

export const useCampaignRecipientsQuery = (
  cid: string | null,
  params: CampaignRecipientsParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery<CampaignRecipientsResponse>({
    queryKey: lifecycleKeys.campaignRecipients(cid ?? '', params),
    queryFn: withErrorHandling(async () => {
      return unwrap(
        request<APIResponse<CampaignRecipientsResponse>>(
          `${CAMPAIGNS}/${cid}/recipients${qs(params as Record<string, unknown>)}`,
        ),
      );
    }, 'Failed to fetch recipients'),
    enabled: enabled && !!cid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useCampaignAudiencePreviewQuery = (cid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<AudiencePreviewResult>({
    queryKey: lifecycleKeys.campaignAudience(cid ?? ''),
    queryFn: withErrorHandling(async () => {
      return unwrap(
        request<APIResponse<AudiencePreviewResult>>(`${CAMPAIGNS}/${cid}/audience/preview`),
      );
    }, 'Failed to preview audience'),
    enabled: enabled && !!cid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Segments ─────────────────────────────────────────────────────────────────

export const useSegmentListQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<SegmentsListResponse>({
    queryKey: lifecycleKeys.segmentList(),
    queryFn: withErrorHandling(async () => {
      return unwrap(request<APIResponse<SegmentsListResponse>>(SEGMENTS));
    }, 'Failed to fetch segments'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    // A segment made in another tab shows up when the admin comes back.
    refetchOnWindowFocus: 'always',
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Who is in a saved segment right now (searchable, paged). */
export const useSegmentMembersQuery = (
  sid: string | null,
  params: { q?: string; limit?: number; offset?: number },
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  const qs = new URLSearchParams({
    ...(params.q ? { q: params.q } : {}),
    limit: String(params.limit ?? 25),
    offset: String(params.offset ?? 0),
  }).toString();
  return useQuery<SegmentMembersResult>({
    queryKey: [...lifecycleKeys.segments(), 'members', sid ?? '', qs],
    queryFn: withErrorHandling(
      async () => unwrap(request<APIResponse<SegmentMembersResult>>(`${SEGMENTS}/${sid}/members?${qs}`)),
      'Failed to load segment members',
    ),
    enabled: enabled && !!sid,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** Values for the segment filter pickers, with how many people hold each. */
export const useSegmentOptionsQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<SegmentOptions>({
    queryKey: [...lifecycleKeys.segments(), 'options'],
    queryFn: withErrorHandling(
      async () => unwrap(request<APIResponse<SegmentOptions>>(`${SEGMENTS}/options`)),
      'Failed to load filter options',
    ),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSegmentDetailQuery = (sid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery<Segment>({
    queryKey: lifecycleKeys.segmentDetail(sid ?? ''),
    queryFn: withErrorHandling(async () => {
      const raw = await unwrap(
        request<APIResponse<Segment | { segment: Segment }>>(`${SEGMENTS}/${sid}`),
      );
      return asSegment(raw);
    }, 'Failed to fetch segment'),
    enabled: enabled && !!sid,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
